import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { validateLaborCode } from 'utils/validateSettings';
import { LaborCodeSetting } from 'types/types';

const useStyles = makeStyles((theme: any) => ({
  container: {
    marginTop: theme.spacing(1)
  },
  question: {
    fontWeight: 600,
    fontSize: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  labelsContainer: {
    marginTop: theme.spacing(2),
    maxWidth: 400
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5)
  },
  labelIndex: {
    fontWeight: 600,
    fontSize: theme.spacing(1.75),
    width: 30,
    color: theme.palette.text.secondary
  },
  sectionLabel: {
    fontWeight: 600,
    fontSize: theme.spacing(1.75),
    marginBottom: theme.spacing(1)
  },
  mandatoryStar: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(0.5)
  },
  levelDetails: {
    marginTop: theme.spacing(2)
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: theme.spacing(1)
  },
  noteText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem'
  }
}));

type LaborCodeSectionProps = {
  initialSelectValue: LaborCodeSetting | undefined;
  setSave: React.Dispatch<
    React.SetStateAction<Record<string, string | boolean>>
  >;
  isFieldInvalid?: boolean;
  setInvalidFields?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  settingId?: string;
};

const MAX_LEVELS = 5;

export const LaborCodeSection: React.FC<LaborCodeSectionProps> = ({
  initialSelectValue,
  setSave,
  isFieldInvalid,
  setInvalidFields,
  settingId = 'laborCodeSetting'
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const [configureLaborCode, setConfigureLaborCode] = useState<boolean>(
    initialSelectValue?.configureLaborCode ?? false
  );
  const [levels, setLevels] = useState<string[]>(
    Array.isArray(initialSelectValue?.levels) ? initialSelectValue!.levels : []
  );
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );

  const validate = useCallback(
    (configure: boolean, currentLevels: string[]): string | null => {
      const messageId = validateLaborCode({
        configureLaborCode: configure,
        levels: currentLevels
      });
      return messageId ? formatMessage({ id: messageId }) : null;
    },
    [formatMessage]
  );

  const updateSave = useCallback(
    (configure: boolean, currentLevels: string[]) => {
      const setting: LaborCodeSetting = {
        configureLaborCode: configure,
        levels: configure ? currentLevels : []
      };
      setSave((prev: any) => ({ ...prev, laborCodeSetting: setting }));
    },
    [setSave]
  );

  const clearInvalid = useCallback(() => {
    if (isFieldInvalid && setInvalidFields) {
      setInvalidFields((prev) => ({ ...prev, [settingId]: false }));
    }
  }, [isFieldInvalid, setInvalidFields, settingId]);

  useEffect(() => {
    updateSave(configureLaborCode, levels);
  }, [configureLaborCode, levels, updateSave]);

  const handleConfigureChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === 'yes';
      setConfigureLaborCode(value);
      if (!value) {
        setValidationMessage(null);
      } else {
        setValidationMessage(validate(true, levels));
      }
      clearInvalid();
    },
    [levels, validate, clearInvalid]
  );

  const handleAddLevel = useCallback(() => {
    if (levels.length >= MAX_LEVELS) return;
    const updated = [...levels, ''];
    setLevels(updated);
    setValidationMessage(validate(configureLaborCode, updated));
    clearInvalid();
  }, [levels, configureLaborCode, validate, clearInvalid]);

  const handleRemoveLevel = useCallback(
    (index: number) => {
      const updated = levels.filter((_, i) => i !== index);
      setLevels(updated);
      setValidationMessage(validate(configureLaborCode, updated));
      clearInvalid();
    },
    [levels, configureLaborCode, validate, clearInvalid]
  );

  const handleLabelChange = useCallback(
    (index: number, value: string) => {
      const updated = levels.map((l, i) => (i === index ? value : l));
      setLevels(updated);
      setValidationMessage(validate(configureLaborCode, updated));
      clearInvalid();
    },
    [levels, validate, configureLaborCode, clearInvalid]
  );

  const showLevelErrors = Boolean(isFieldInvalid) && configureLaborCode;
  const requiredFieldHelperText = formatMessage({
    id: 'intermediateInstallDialog.inputError'
  });

  return (
    <Box className={classes.container}>
      <Typography className={classes.question}>
        <FormattedMessage id="laborCodeSection.question" />
      </Typography>
      <FormControl>
        <RadioGroup
          row
          value={configureLaborCode ? 'yes' : 'no'}
          onChange={handleConfigureChange}
        >
          <FormControlLabel
            value="no"
            control={<Radio />}
            label={formatMessage({ id: 'laborCodeSection.no' })}
            data-testid="labor-code-no"
          />
          <FormControlLabel
            value="yes"
            control={<Radio />}
            label={formatMessage({ id: 'laborCodeSection.yes' })}
            data-testid="labor-code-yes"
          />
        </RadioGroup>
      </FormControl>

      {configureLaborCode && (
        <Box className={classes.levelDetails}>
          <Box className={classes.labelsContainer}>
            <Typography className={classes.sectionLabel}>
              <FormattedMessage id="laborCodeSection.sectionLabel" />
              <span className={classes.mandatoryStar}>*</span>
            </Typography>
            {levels.map((level, index) => {
              const isEmpty = !level || !level.trim();
              const showError = showLevelErrors && isEmpty;
              return (
                <Box
                  key={`labor-code-level-${index}`}
                  className={classes.labelRow}
                >
                  <Typography className={classes.labelIndex}>
                    {index + 1}.
                  </Typography>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={level}
                    onChange={(e) => handleLabelChange(index, e.target.value)}
                    placeholder={formatMessage(
                      { id: 'laborCodeSection.levelPlaceholder' },
                      { index: index + 1 }
                    )}
                    error={showError}
                    helperText={showError ? requiredFieldHelperText : undefined}
                    inputProps={{
                      'data-testid': `labor-code-label-${index}`
                    }}
                  />
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleRemoveLevel(index)}
                    data-testid={`remove-level-${index}`}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
            {levels.length < MAX_LEVELS && (
              <IconButton
                color="primary"
                size="small"
                onClick={handleAddLevel}
                className={classes.addButton}
                data-testid="add-level-button"
              >
                <AddCircleOutlineIcon fontSize="small" />
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  <FormattedMessage id="laborCodeSection.addLevel" />
                </Typography>
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {validationMessage && (
        <Typography className={classes.noteText} data-testid="labor-code-note">
          <FormattedMessage id="laborCodeSection.notePrefix" />
          {validationMessage}
        </Typography>
      )}
    </Box>
  );
};
